/* eslint-disable */
import Vue from "vue";
import * as d3 from "d3";
import XLSX from "xlsx";
import pptxgen from "pptxgenjs";
import moment from "moment";
import ApiRequest from "@/helpers/ApiRequest";
import GetHeaders from "@/helpers/GetHeaders";
import store from "../../../../store/store";
import _ from "lodash";

/**
 *
 * @param arrayId: array of layer objects
 * @returns {*}
 */
function joinArrayId(arrayId) {
  return arrayId.map(x => x ? escapeHtml(x.name) : "All").join(' - ');
}

/**
 * Extract (sub)category and type information on layers
 * @param layers
 * @param data
 * @returns {{types: {}, categories: {}, categoriesConv: {}}}
 */
function extractTypesCategories(layers, data) {
    // Will extract all types and categories, but also immediately add it to the corresponding data point
    // Types
    let types = {};
    let categories = {};
    let categoriesConv = {}; // Webkey -> name
    for (let dl of data) {
        let cl = dl.x;
        let layer = layers[cl];
        let t = layer.map(x => x ? x.type : "All").join(" - ");
        if (t in types) { types[t] += 1 } else { types[t] = 1 }

        let cats = [];
        for (let l of layer) {
            if (l) {
                if (l.category) { cats.push(...l.category) }
                if (l.subcategory) { cats.push(...l.subcategory) }
            }
        }

        dl.type = t;
        dl.categories = cats.map(x => x[0]);
        // Testing for duplicates - only add one category per layer
        let tested = new Set();
        for (let c of cats) {
            if (!(tested.has(c[0]))) {
                tested.add(c[0]);
                if (c[0] in categories) {
                    categories[c[0]] += 1
                } else {
                    categoriesConv[c[0]] = c[1];
                    categories[c[0]] = 1
                }
            }
        }

    }
    return { types: types, categories: categories, categoriesConv: categoriesConv }

}

/**
 * Create a scale, returns necessary objects and information
 * @param on
 * @param data
 * @param domain: array of domain (min and max)
 * @returns {{field: string, scale: (*|boolean|number|string), description: string[], location: string, label: string, sizeMax: *}}
 */
function getScale(on, data, domain = undefined) {
    let location, scale, label, description, type, sizeMax;
    let log = false;
    let decimalFormat = false;
    if (on.split('_').length === 2) {
        [on, type] = on.split('_');
        if (type === "log") log = true;
    }
    if (log) { scale = d3.scaleLog() } else { scale = d3.scaleLinear() }
    if (on === "recency") {
        location = ['data', 'recency'];
        scale = scale.domain(domain || [-3, 3]).clamp(true);
        label = "Recency";
        decimalFormat = true;
        description = ['Recency is the log2 of the chance that the item is found in recent documents versus older documents (Log2 Odds Ratio).',
            'A value of 2 thus means a 4x higher chance to retrieve the document in recent documents.',
            'Any value lower than -3 and higher than 3 is clamped to these respective values.'
        ]
    } else if (on === "filtrecency") {
        location = ['filtdata', 'recency'];
        scale = scale.domain(domain || [-3, 3]).clamp(true);
        label = "Recency (Filter)";
        decimalFormat = true;
        description = ['Recency is the log2 of the chance that the item is found in recent documents versus older documents (Log2 Odds Ratio).',
            'A value of 2 thus means a 4x higher chance to retrieve the document in recent documents.',
            'Any value lower than -3 and higher than 3 is clamped to these respective values.',
            'This metric is linked with the applied filter.'
        ]
    } else if (on === "size") {
        location = ['data', 'origsize'];
        if (domain) {
            scale = scale.domain([domain[0], domain[1] * 1.1]);

            sizeMax = domain[1];
        } else {
            sizeMax = data.length ? data.reduce((a, b) => d3.max([(a.data ? a.data.origsize : a), (b.data ? b.data.origsize : 0)])) : 0;
            scale = scale.domain([log ? 1 : 0, sizeMax * 1.1]);
        }
        label = "Size";
        description = [`Size of the document pool between 1 and ${sizeMax} documents in the applied year range.`]
    } else if (on === "origsize") {
        location = ['data', 'origsize'];
        if (domain) {
            scale = scale.domain([domain[0], domain[1] * 1.1]);
            sizeMax = domain[1];
        } else {
            sizeMax = data.reduce((a, b) => d3.max([(a.data ? a.data.origsize : a), (b.data ? b.data.origsize : 0)]));
            scale = scale.domain([log ? 1 : 0, sizeMax * 1.1]);
        }
        label = "Original Size";
        description = [`Size of the document pool between 1 and ${sizeMax} documents in the complete document pool.`]
    } else if (on === "filtsize") {
        location = ['filtdata', 'size'];
        if (domain) {
            scale = scale.domain([domain[0], domain[1] * 1.1]);
            sizeMax = domain[1];
        } else {
            sizeMax = data.reduce((a, b) => d3.max([(a.filtdata ? a.filtdata.size : a), (b.filtdata ? b.filtdata.size : 0)]));
            scale = scale.domain([log ? 1 : 0, sizeMax * 1.1]);
        }

        label = "Size (Filter)";
        description = [`Size of the document pool between 1 and ${sizeMax} documents for the applied filter.`]
    } else if (on === "linked") {
        location = ['filtdata', 'linked'];
        scale = scale.domain(domain || [0, 1]);
        label = "Linked (Filter)";
        decimalFormat = true;
        description = ['Linked is a metric specific for a filter and represent the connection between the item and the filter.',
            'It is the ratio of documents of the item that can be found in the applied filter of the item.',
            'A value of 1 thus means that all documents are in the applied filter, and show a very strong link between the item and the filter.'
        ]
    } else if (on === "relrecency") {
        location = ['filtdata', 'relrecency'];
        scale = scale.domain(domain || [-3, 3]).clamp(true);
        label = "Relative Recency (Filter)";
        decimalFormat = true;
        description = ['Relative recency is a normalized recency of the filter in comparison to the complete pool.',
            'A certain item can be quite old (eg. -0.5) in the complete pool, but a very new element (eg. 2) for a given filter.',
            'The relative recency will then be 2.5, and indicates that item is more recent in comparison to the complete pool.'
        ]
    } else if (on === "relsize") {
        location = ['filtdata', 'relsize'];
        scale = scale.domain(domain || [-1, 1]);
        label = "Relative Size (Filter)";
        description = ['Relative size is the normalized size of the filter in comparison to the complete pool.',
            'The value will be between -1 (unrelated) and 1 (strongly related)'
        ]
    }

    return { scale: scale, location: location[0], field: location[1], label: label, description: description,
      sizeMax: sizeMax, log: log, decimalFormat: decimalFormat}
};

/**
 *
 * @param on
 * @param processedData
 * @oaram domain: pass on domain instead of processedData
 * @returns {{interpolator: (function(...[*]=)), dataLabel: string, scale: *}}
 */
function getColorScale(on, processedData, domain = undefined) {
    let interpolator, dataLabel;
    let { scale, sizeMax } = getScale(on, processedData, domain);
    
    if (on === "recency_linear") {
        scale = scale.range([1, 0]);
        //interpolator = d3.interpolateRdBu;
        interpolator = d3.interpolateRgbBasis(["#F07E2A", "#ffbf0d", "#ffe6a0"]);
        dataLabel = "recency";
    } else if (on === "size_log") {
        scale = scale.range([0, 1]);
        //scale.domain([1, sizeMax * 1.1]);
        interpolator = d3.interpolate("#2FFFE7", "#00696b");
        dataLabel = "size";
    } else if (on === "origsize_log") {
        scale = scale.range([0, 1]);
        //scale.domain([1, sizeMax * 1.1]);
        interpolator = d3.interpolate("#2FFFE7", "#00696b");
        dataLabel = "origsize";
    } else if (on === "filtrecency_linear") {
        scale = scale.range([1, 0]);
        interpolator = d3.interpolateRgbBasis(["#F07E2A", "#ffbf0d", "#ffe6a0"]);
        //interpolator = d3.interpolateDiscrete(["red", "green", "blue"]);
        dataLabel = "recency";
    } else if (on === "filtsize_log") {
        scale = scale.range([0, 1]);
        //scale = scale.clamp(true);
        //scale.domain([1, sizeMax * 1.1]);
        interpolator = d3.interpolate("#2FFFE7", "#00696b");
        dataLabel = "size";
    } else if (on === "linked_linear") {
        scale = scale.range([0, 1]);
        //scale.domain([1, sizeMax * 1.1]);
        interpolator = d3.interpolateOranges;
        dataLabel = "linked";
    } else if (on === "relrecency_linear") {
        scale = scale.range([1, 0]);
        //scale.domain([1, sizeMax * 1.1]);
        interpolator = d3.interpolateRgbBasis(["#F07E2A", "#ffbf0d", "#ffe6a0"]);
        //interpolator = d3.interpolateDiscrete("#F07E2A", "#FFFF00", "#909090");
        dataLabel = "relrecency";
    } else if (on === "relsize_linear") {
        scale = scale.range([1, 0]);
        //scale.domain([1, sizeMax * 1.1]);
        interpolator = d3.interpolate("#2FFFE7", "#00696b");
        dataLabel = "relsize";
    }
    return { scale: scale, interpolator: interpolator, dataLabel: dataLabel }
}

/**
 * Get Z-axis dropdown options on analysis
 * @param zLayersBase
 */
function getUpdatedZ(zLayersBase) {
    return zLayersBase.map(function(arr, c) {
        let text = joinArrayId(arr); // Join all array elements - if null replace by "All"
        return { 'value': c, 'text': text }
    }).sort((a, b) => (a.text > b.text) ? 1 : -1); // Alphabetical sort
}

/**
 * Returns available metrics
 * @param processedData: processedData array
 */
function getUpdatedMetrics(processedData) {
    const options = {
        "size": "Size",
        "recency": "Recency",
        "origsize": "Original Size",
        "filtsize": "Size (filter)",
        "filtrecency": "Recency (filter)",
        "relsize": "Relative size (filter)",
        "relrecency": "Relative recency (filter)",
        "linked": "Linked (filter)"
    };
    if (processedData.length > 0) {
        let opts = [];
        let obj = processedData[0]; // Element where we fetch information from
        if ("data" in obj) {
            Object.keys(obj["data"]).forEach(key => { opts.push({ 'value': key, 'text': options[key] }) })
        }
        if ("filtdata" in obj) {
            Object.keys(obj["filtdata"]).forEach(key => {
                if (["size", "recency"].includes(key)) {
                    opts.push({ 'value': "filt" + key, 'text': options["filt" + key] })
                } else {
                    opts.push({ 'value': key, 'text': options[key] })
                }
            })
        }
        return opts.sort((a, b) => (a.text > b.text) ? 1 : -1)
    } else {
        return {}
    }
}

/**
 * Load data for analysis
 * @param q
 * @returns {Promise<Response>}
 */
function loadData(q) {
  return ApiRequest
    .explorePost(
      'analyze_data',
      JSON.parse(q),
      GetHeaders.dataHeaders()
    )
    .then(res => {
      return res.data;
    })
    .catch(error => {
      //Todo: error code blocked temporarily as payload is not in control
      //store.dispatch('setServerNotice', { code: error.response.status, msg: error.response.statusText } );
      return error
    });
}

/**
 * Load HierarchyData (mainly for testing purposes)
 * @returns {Promise<Response>}
 */
function loadHierarchy() {
    const body = JSON.stringify({ "hu_id": "test", "init": true, "recentyear": null, "startyear": null, "stopyear": null, "lowerlimit": 7 });
    const payload = {
        body: body,
        method: 'POST',
        headers: {
            "Authorization": localStorage.getItem('jwt'),
            "Requested-Data-Source": "data"
        }
    };

    const url = Vue.prototype.$globalSettings.api_endpoint + '/hierarchydata';
    return fetch(url, payload, )
}

function ellipsis(s, size) {
    return s.length > size ? s.slice(0, size - 2) + '...' : s
}

function createToolTip(nodes, event) {
    d3.select('#hovertooltip').remove();
    let data = '';
    if (!(nodes instanceof Array)) {
        nodes = [nodes]
    }

    let themeCntr = 0;
    let bgcolor = '#73bcb9';
    for (let i = 0; i < nodes.length; i++) {
        let d = nodes[i];
        let el = d.node || d;
        if (el.type === "Document") {
            // Document
            bgcolor = '#bca6a7';
            let title, dat = "NA";
            el.text.forEach(textEl => {
                if (textEl.name === "Title") {
                    title = escapeHtml(textEl.text);
                } else if (textEl.name === "Publication Date") {
                    dat = escapeHtml(textEl.text);
                }
            });

            data += escapeHtml(title) + " (" + escapeHtml(dat) + ")<br>";
            // Fields
            d.fields.sort((a, b) => b[1] - a[1]).forEach(function(fieldVal) {
                let v = (fieldVal[1] * 100).toFixed(2) + '%';
                let field = fieldVal[0];
                data += '<br><span class="mr-2"><strong>' + escapeHtml(v) + '</strong></span>\t\t' + escapeHtml(field);
            });
        } else if (el.type === "Arc") {
            data += escapeHtml(el.name) + "<br>" + escapeHtml(el.size) + " documents"
        } else if (!el.size) {
            data += escapeHtml(el.name)
        } else {
            data += "<strong>" + escapeHtml(el.type) + " : </strong>" + escapeHtml(el.name) + " (" + escapeHtml(el.data.size) + ")<br>"
        }
    }

    // It is important to already create object before doing correction, since otherwise height/width of div cannot
    // be determined
    let left = (d3.event || event).pageX + 10;
    let top = (d3.event || event).pageY + 10;
    let div = d3.select('body').append("div")
        .attr('id', 'hovertooltip')
        .style('left', left + 'px')
        .style('top', top + 10 + 'px')
        .html(data);

    // Calculate and correct position
    div.style('background-color', bgcolor);
    let height = div.attr('height');
    let width = div.attr('width');
    let cWidth = document.documentElement.clientWidth;
    let cHeight = document.documentElement.clientHeight;
    let overY = top + height - cHeight;
    let overX = left + width - cWidth;

    // If div goes out of bounds => correct it
    if (overY > 0) {
        div.style('top', top - overY - 30);
    }
    if (overX > 0) {
        div.style('left', left - overX - 30);
    }
}

function convertDataArrayToObject(data, configuration = "data") {
    //console.log(data);
    let keys;
    switch (configuration) {
        case "data":
            keys = ["origsize", "size", "recency", "avg_silhouette"];
            break;
        case "filtdata" || "focusdata":
            keys = ['size', 'recency', 'linked', 'relrecency', 'relsize']
            break;
    }
    let o = {};
    data.forEach((val, c) => {
      //console.log(val);
        if (val === "inf") {
            val = Infinity
        } else if (val === "-inf") {
            val = -Infinity
        }
        o[keys[c]] = val;
    });
    return o;
}

/**
 * Based on mbostock: https://observablehq.com/@mbostock/saving-svg
 * @param svg svgnode
 * @returns {Blob}
 */
function exportSvg(svg) {
  const xmlns = "http://www.w3.org/2000/xmlns/";
  const xlinkns = "http://www.w3.org/1999/xlink";
  const svgns = "http://www.w3.org/2000/svg";

  svg = svg.cloneNode(true);
  // Set font on all text elements
  svg.querySelectorAll('text').forEach(el => {
    el.setAttribute('font-family', 'Montserrat, Arial, sans-serif');
    // el.setAttribute('font-size', '14px'); // Optional
  });
  const fragment = window.location.href + "#";
  const walker = document.createTreeWalker(svg, NodeFilter.SHOW_ELEMENT, null, false);
  while (walker.nextNode()) {
    for (const attr of walker.currentNode.attributes) {
      if (attr.value.includes(fragment)) {
        attr.value = attr.value.replace(fragment, "#");
      }
    }
  }
  svg.setAttributeNS(xmlns, "xmlns", svgns);
  svg.setAttributeNS(xmlns, "xmlns:xlink", xlinkns);
  const serializer = new window.XMLSerializer;
  const string = serializer.serializeToString(svg);
  return new Blob([string], {type: "image/svg+xml"});
}

/**
 * Based on mbostock: https://observablehq.com/@mbostock/saving-svg
 * @param svg svgnode
 * @param toblob return blob or dataurl
 * @returns {*}
 */
function exportSvg2Png(svg) {
  let resolve, reject;
  const promise = new Promise((y, n) => (resolve = y, reject = n));
  const image = new Image;

  const canvas = document.createElement('canvas');

  image.onerror = reject;
  image.onload = () => {
    const rect = svg.getBoundingClientRect();
    const ctx = canvas.getContext("2d");
    ctx.canvas.width = rect.width;
    ctx.canvas.height = rect.height;
    ctx.drawImage(image, 0, 0, rect.width, rect.height);
    ctx.canvas.toBlob(resolve);
  };
  image.src = URL.createObjectURL(exportSvg(svg));
  return promise;
}

function downloadSvg(svg) {
  let blob = exportSvg(svg);
  let link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = "Mynd- " + store.state.api.currentModelName + " -Fieldmap.svg";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function downloadSvg2Png(svg) {
  let promise = exportSvg2Png(svg);
  promise.then((blob) => {
    let link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = "Mynd- " + store.state.api.currentModelName + " -Fieldmap.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  });
}

function blobToDataURL(blob, callback) {
    let a = new FileReader();
    a.onload = function(e) {callback(e.target.result);}
    a.readAsDataURL(blob);
}

async function downloadPpt(svg) {
  let pres = new pptxgen();
  let slide = pres.addSlide();
  //let blob = exportSvg(svg);
  let blob = await exportSvg2Png(svg);
  blobToDataURL(blob, (dataurl) => {
    slide.addImage({"data": dataurl, x:0, y:0.2, w:10, h:5});
    pres.writeFile("Mynd- " + store.state.api.currentModelName + " -Fieldmap.pptx")}
    );
}

async function downloadXls(cd) {
  if(cd.processedData){
    if(cd.yLayers[0][0]){
      let el = cd.processedData[0];
      let dataKeys = Object.keys(el.data);
      let filtDataKeys = "filtdata" in el ? Object.keys(el.filtdata) : [];
      let allKeys = dataKeys.concat(filtDataKeys);
      //let yLayers = cd.yLayers.map(x => x.map(y => y && y.name ? y.name : "All").join(" - "));
      let xLayers = cd.xLayers.map(x => x.map(y => y && y.name ? y.name : "All").join(" - "));
      let headers = [""].concat(xLayers);
      let fName = "mynd-output-"+moment().unix()+".xlsx";
      let wb = XLSX.utils.book_new();

      for (let mc = 0; mc < allKeys.length; mc++) {
        let dataLoc = mc < dataKeys.length ? "data": "filtdata";
        let metric = allKeys[mc];
        let res = [headers];
        for (let i = 0; i < cd.yLayers.length; i++) {
          let xLabel = cd.yLayers[i].map(x => x ? (x && x.name ? x.name : "All") : "All").join(" - ");
          let xVals = cd.processedData.filter(d => d.y === i).sort((a, b) => a.y ? a.y - b.y : a - b.y);
          let arr = new Array(cd.xLayers.length);

          if (xVals.length > 0) {
            xVals.forEach((el,c) => {
              arr[el.x] = el[dataLoc][metric];
            });
          }
          res.push([xLabel].concat(arr))
        }
        let ws = XLSX.utils.aoa_to_sheet(res);
        XLSX.utils.book_append_sheet(wb, ws, metric + (dataLoc === "data" ? "" : " (Filter)"));
      }
      XLSX.writeFile(wb, fName);
    }
    else{
      let el = cd.processedData[0];
      let dataKeys = Object.keys(el.data);
      let filtDataKeys = "filtdata" in el ? Object.keys(el.filtdata) : [];
      let allKeys = dataKeys.concat(filtDataKeys);
      let yLayers = cd.yLayers.map(x => x.map(y => y && y.name ? y.name : "All").join(" - "));
      //let xLayers = cd.xLayers.map(x => x.map(y => y && y.name ? y.name : "All").join(" - "));
      let headers = [""].concat(yLayers);
      let fName = "mynd-output-"+moment().unix()+".xlsx";
      let wb = XLSX.utils.book_new();

      for (let mc = 0; mc < allKeys.length; mc++) {
        let dataLoc = mc < dataKeys.length ? "data": "filtdata";
        let metric = allKeys[mc];
        let res = [headers];
        for (let i = 0; i < cd.xLayers.length; i++) {
          let xLabel = cd.xLayers[i].map(x => x ? (x && x.name ? x.name : "All") : "All").join(" - ");
          let xVals = cd.processedData.filter(d => d.x === i).sort((a, b) => a.y ? a.y - b.y : a - b.y);
          let arr = new Array(cd.xLayers.length);

          if (xVals.length > 0) {
            xVals.forEach((el,c) => {
              arr[el.y] = el[dataLoc][metric];
            });
          }
          res.push([xLabel].concat(arr))
        }
        let ws = XLSX.utils.aoa_to_sheet(res);
        XLSX.utils.book_append_sheet(wb, ws, metric + (dataLoc === "data" ? "" : " (Filter)"));
      }
      XLSX.writeFile(wb, fName);
    }
  }
  else{
    let sheetData = cd.map(r => {
      return {
        "Name": r.label,
        "X": r.x,
        "Y": r.y,
        "Size": r.size,
        "Recency": r.recency,
        "Cluster": r.cluster
      }
    })
    let fName = "mynd-output-"+moment().unix()+".xlsx";
    let ws = XLSX.utils.json_to_sheet(sheetData);
    let wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws);
    XLSX.writeFile(wb, fName);
  }

}

async function dataForDimRed(data, r, c) {
  let fData = [];
  let rCounter = 0;
  let cCounter = 0;
  let columns = [];
  return new Promise((resolve, reject) => {
    data.forEach((row, n) => {
      if(rCounter === row.y){
        if(cCounter === row.x){
          columns.push(row.size);
          cCounter += 1;
        }
        else{
          while(cCounter < row.x){
            columns.push(0);
            cCounter += 1;
          }
          columns.push(row.size);
          cCounter += 1;
        }
      }
      else{
        while(cCounter < c){
          columns.push(0);
          cCounter += 1;
        }
        fData.push(columns);
        columns = [];
        rCounter += 1;
        cCounter = 0;
        if(cCounter === row.y){
          columns.push(row.size);
          cCounter += 1;
        }
        else{
          while(cCounter < row.x){
            columns.push(0);
            cCounter += 1;
          }
          columns.push(row.size);
          cCounter += 1;
        }
      }
      if(data.length === n+1){
        while(cCounter < c){
          columns.push(0);
          cCounter += 1;
        }
        fData.push(columns);
        normalizeData(fData).then(res => {
          resolve(res);
        }) 
      }
    })
  });
}

async function normalizeData(data){
  return new Promise((resolve, reject) => {
    let fData = [];
    let rCounter = 0;
    let cCounter = 0;
    data.forEach((row, r) => {
      fData.push([]);
      let rowSum = _.sum(row);
      //console.log(rowSum);
      cCounter = 0;
      let colIter = new Promise((colResolve, colReject) => {
        row.forEach((col) => {
          col===0? fData[r].push(col):fData[r].push(col/rowSum);
          cCounter += 1;
        });
        if(row.length === cCounter){
         colResolve();
        }
      })
      colIter.then(() => {
        rCounter += 1;
        if(data.length === rCounter){
          resolve(fData);
        }
      })
    })
  })
}

async function calculateSR(layer, ry, sy, ey){
  return new Promise((resolve, reject) => {
    let newQuery = {
      "xlayers": layer,
      "ylayers":[],
      "zlayers":[],
      "subset_filters":[],
      "startyear":sy,
      "recentyear":ry,
      "stopyear":ey,
      "postprocessing":[]
    }
    loadData(JSON.stringify(newQuery)).then(res => {
      //console.log(res);
      let srs = res.data['0'];
      let val = [];
      Object.entries(srs).forEach((item, n) => {
        let [key, value] = item;
        let i = key.split('_')[0];
        val.push({place: i, values: value.data});
        //console.log(item, n);
        if((n+1) === Object.entries(srs).length){
          //console.log(val);
          resolve(val);
        }
      });
    })  
  })
}

function getRecency(r){
  if(r === 'inf'){
    return 3
  }
  else if(r === '-inf'){
    return -3
  }
  else{
    return r
  }
}

function setScalesAxis(vis, group, invertX=false, invertY=false, normalOrient=true) {
  let xAxis = group.select('#gXAxis');
  let yAxis = group.select('#gYAxis');
  let xLabel = group.select('#gXAxisLabel');
  let yLabel = group.select('#gYAxisLabel');
  let xScale = Object.assign({}, vis.xScale);
  let yScale = Object.assign({}, vis.yScale);

  if (invertX) {
    let dom = xScale.scale.domain();
    xScale.scale.domain([dom[1], dom[0]])
  }
  if (invertY) {
    let dom = yScale.scale.domain();
    yScale.scale.domain([dom[1], dom[0]])
  }
  if (!(xAxis.empty())) {
    xAxis.call(
      normalOrient
        ? d3.axisBottom(xScale.scale).tickFormat(tickFormatter(xScale))
        : d3.axisTop(xScale.scale).tickFormat(tickFormatter(xScale)).tickPadding(9).tickSize(3)
    );
  }
  if (!(yAxis.empty())) {
    yAxis.call(
      normalOrient
        ? d3.axisLeft(yScale.scale).tickFormat(tickFormatter(yScale))
        : d3.axisRight(yScale.scale).tickFormat(tickFormatter(yScale))
    );
  }
  if (!(xLabel.empty())) {
    xLabel.call(labelHover, vis, true)
  }
  if (!(yLabel.empty())) {
    yLabel.call(labelHover, vis, false)
  }
}

function tickFormatter(scale) {
  if (scale.decimalFormat) {
    return d3.format(".2f");
  }

  return d3.format("d");
}

function labelHover(label, vis, onX) {
  let scale = onX ? vis.xScale : vis.yScale;
  label.on('mouseover', () => d3.select('#hoverDiv')
        .style('opacity', 0.9)
        .style('display', 'block')
        .style('left', d3.event.pageX + 10 + "px")
        .style('top', d3.event.pageY + 10 + "px")
        .html(`${scale.description.join('<br>')}`))
      .on('mouseout', () => d3.select('#hoverDiv').style('opacity', 0))
      .select('text')
      .text(escapeHtml(scale.label));
}

function createAxis(vis, group, onX) {
  let axisId, axisFunc, scale;
  if (onX) {
    axisId = 'gXAxis';
    axisFunc = d3.axisBottom;
    scale = vis.xScale.scale
  } else {
    axisId = 'gYAxis';
    axisFunc = d3.axisLeft;
    scale = vis.yScale.scale;
  }
  let axisLabelId = axisId + "Label";
  let gAxis = group.select('g#' + axisId);
  let gAxisLabel = group.select('g#' + axisLabelId);

  if (gAxis.empty()) {
    gAxis = group.append('g').attr('id', axisId).call(axisFunc(scale));
    gAxisLabel = group.append('g').attr('id', axisLabelId);
    let gAxisLabelText = gAxisLabel.append('text')
        .attr('text-anchor', 'middle')
        .attr('font-weight', 'bold');

    if (! onX) { gAxisLabelText.attr('transform', 'rotate(-90)');}
  }
  return [gAxis, gAxisLabel]
}

function escapeHtml(unsafe) {
  if (! unsafe) {
    return null;
  }
  unsafe = unsafe.toString();
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export {
  convertDataArrayToObject,
  createAxis,
  createToolTip,
  downloadPpt,
  downloadSvg,
  downloadSvg2Png,
  downloadXls,
  dataForDimRed,
  calculateSR,
  ellipsis,
  escapeHtml,
  extractTypesCategories,
  getColorScale,
  getScale,
  getUpdatedMetrics,
  getUpdatedZ,
  joinArrayId,
  loadData,
  loadHierarchy,
  setScalesAxis,
}
